import { ApplicationConfig, importProvidersFrom, inject, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { JwtModule } from '@auth0/angular-jwt';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { routes } from './app.routes';
import { authReducer } from './ngrx/auth/auth.reducer';
import { templateReducer } from './ngrx/template/template.reducers';
import { RouterEffects, TemplateEffects } from './ngrx/template/template.effects';
import { AuthEffects } from './ngrx/auth/auth.effects';
import { authInterceptor } from './interceptors/auth.interceptor';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { da } from 'date-fns/locale';
import { registerLocaleData } from "@angular/common";
import localeDa from '@angular/common/locales/da';
import { DataEffects } from './ngrx/data/data.effects';
import { dataReducer } from './ngrx/data/data.reducer';
import { TokenStateService } from './services/token-state/token-state.service';
import { environment } from './environment/environment';
import { registerLicense } from '@syncfusion/ej2-base';
import { ScheduleModule, RecurrenceEditorModule } from '@syncfusion/ej2-angular-schedule';
import { metaReducers } from './ngrx/store-reset.metareducer';
import { dynamicTableQuestion } from './surveyJS-custom-questions/dynamic-table-question';
import { DashboardService, DASHBOARD_SERVICE_TOKEN } from './services/dashboard/dashboard.service';
import { ChartService, CHART_SERVICE_TOKEN } from './services/dashboard/chart.service';
import { dashboardReducer } from './ngrx/dashboard/dashboard.reducer';
import { DashboardEffects } from './ngrx/dashboard/dashboard.effects';
import { eventsReducer } from './ngrx/events/events.reducer';
import { EventsEffects } from './ngrx/events/events.effects';
import { DunaDashboardModule } from './components/duna/duna-dashboard/duna-dashboard.module';
import { DashboardLayoutService } from './services/dashboard/dashboard-layout.service';
import { UpdateNotificationService } from './services/update-notification/update-notification.service';
import { PeriodicUpdateCheckerService } from './services/periodic-update-checker/periodic-update-checker.service';
import { VersionService } from './services/version/version.service';
import { LoadingService } from './services/loading/loading.service';
import { DisplayService } from './services/display/display.service';
import { AuthAppLifecycleService } from './services/auth/auth-app-lifecycle.service';

// Function to fix SurveyJS dropdown issues globally — removed: overrides caused popup flicker in modals
export function fixSurveyJSDropdownIssues() {
  return () => Promise.resolve();
}

//Gamle licens (tror 27.1.58): Ngo9BigBOggjHTQxAR8/V1NDaF5cWWtCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWH5ed3VWRmNYWEV+V0Y=
//31.x.x: Ngo9BigBOggjHTQxAR8/V1JFaF5cXGRCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWH9ecXVTRWZcU0FzWEJWYEg=
//34.x.x: Ngo9BigBOggjHTQxAR8/V1JAaF5cX2pCd1p/TH5YfUNzdUVEY1ZUTXxaS1ZhSXxVdkxjXX1cdXNWRmJaV0d9XEY=
 
registerLicense('Ngo9BigBOggjHTQxAR8/V1JAaF5cX2pCd1p/TH5YfUNzdUVEY1ZUTXxaS1ZhSXxVdkxjXX1cdXNWRmJaV0d9XEY=');

const danishConfig = new DateFnsConfigurationService();
danishConfig.setLocale(da);

registerLocaleData(localeDa);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, 
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload'
      })
    ),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideServiceWorker('/combined-sw.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: () => {
            const tokenStateService = inject(TokenStateService);
            return tokenStateService.getCurrentToken();
          },
          allowedDomains: [environment.rootApiUrl.replace('https://', '').replace('http://', '')],
          disallowedRoutes: [
            `${environment.rootApiUrl}/cache_hash.php`,
            `${environment.rootApiUrl}/api/login`,
            `${environment.rootApiUrl}/api/refresh-token`,
            `${environment.rootApiUrl}/api/check-auth-methods`
          ],
        }
      }),
      MatSnackBarModule,
      ScheduleModule,
      RecurrenceEditorModule,
      DunaDashboardModule
    ),
    provideStore({
      auth: authReducer,
      template: templateReducer,
      data: dataReducer,
      dashboard: dashboardReducer,
      events: eventsReducer
    }, { metaReducers }),
    provideEffects([RouterEffects, TemplateEffects, AuthEffects, DataEffects, DashboardEffects, EventsEffects]),
    {
      provide: DateFnsConfigurationService,
      useValue: danishConfig
    },
    // Register the dashboard services with string-based tokens for lookup during logout
    {
      provide: 'DashboardService',
      useExisting: DashboardService
    },
    {
      provide: 'ChartService',
      useExisting: ChartService
    },
    {
      provide: DASHBOARD_SERVICE_TOKEN,
      useExisting: DashboardService
    },
    {
      provide: CHART_SERVICE_TOKEN,
      useExisting: ChartService
    },
    // Add APP_INITIALIZER from AppModule
    {
      provide: APP_INITIALIZER,
      useFactory: fixSurveyJSDropdownIssues,
      multi: true
    },
    DashboardLayoutService,
    // Initialize update services
    UpdateNotificationService,
    PeriodicUpdateCheckerService,
    VersionService,
    // Add missing service providers for standalone components
    LoadingService,
    DisplayService,
    AuthAppLifecycleService
  ]
};
